import { stripAppBasePath } from "@/utils/env";

/**
 * Safe redirect validation for post-login/post-register redirects.
 * Prevents open redirect: only same-origin paths allowed, no protocol, no "//", no "javascript:".
 */

/** Returns true if path is safe for client-side redirect (same-origin path only). */
export function isSafeRedirectPath(path: string): boolean {
  if (!path || typeof path !== "string") return false;
  const trimmed = path.trim();
  if (!trimmed.startsWith("/")) return false;
  if (trimmed.startsWith("//")) return false;
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("javascript:")) return false;
  if (lower.startsWith("http://") || lower.startsWith("https://")) return false;
  try {
    const u = new URL(trimmed, "https://example.com");
    if (u.protocol !== "https:" || u.host !== "example.com") return false;
  } catch {
    return false;
  }
  return true;
}

/** Normalize browser/saved URLs to Next router paths and recheck after stripping. */
export function toSafeAppRedirect(path: string): string | null {
  if (!isSafeRedirectPath(path)) return null;
  const relative = stripAppBasePath(path.trim());
  if (!isSafeRedirectPath(relative)) return null;
  // Resolve dot segments before Next prefixes the route, so ../ cannot escape
  // a subpath deployment. Preserve the query and fragment on the return trip.
  const url = new URL(relative, "https://example.com");
  return `${url.pathname}${url.search}${url.hash}`;
}

export function getSafeRedirectRedirect(searchParams: URLSearchParams): string | null {
  const redirect = searchParams.get("redirect") ?? searchParams.get("from");
  return redirect ? toSafeAppRedirect(redirect) : null;
}
